import React, { useState } from 'react';

import { Button, DropdownMenu, Text } from '@betterment/samba-react';

import { HouseholdQuery } from './HouseholdSummary/graphql/query.household.generated';

import { useImpersonationModal } from '../../contexts/ImpersonationModalContext';
import urlBuilder from '../../Shared/url-builder';
import FormButton from '../FormButton';

type Household = Extract<HouseholdQuery['household'], { __typename: 'Household' }>;
type Client = Household['members'][number];

interface DropdownItemProps {
  member: Client;
  showImpersonationModal: boolean;
  onMemberSelect: (member: Client) => void;
}

const DropdownItem: React.FC<DropdownItemProps> = ({ member, showImpersonationModal, onMemberSelect }) => {
  if (showImpersonationModal) {
    return (
      <DropdownMenu.Item key={member.id} onSelect={() => onMemberSelect(member)}>
        {member.legalFullName}
      </DropdownMenu.Item>
    );
  }

  return (
    <FormButton
      key={member.id}
      data-testId={`form-${member.id}`}
      method="post"
      action={urlBuilder.getImpersonateClientPage.absolute({ clientId: member.id })}
      shouldOpenNewTab={true}
      variant="ghostSecondary"
      css={{ width: 'size.full', justifyContent: 'start' }}
    >
      <Text>{member.legalFullName}</Text>
    </FormButton>
  );
};

interface MultipleMembersButtonProps {
  members: Client[];
  showImpersonationModal: boolean;
  isDropdownOpen: boolean;
  onDropdownOpenChange: (open: boolean) => void;
  onMemberSelection: (member: Client) => void;
}

const MultipleMembersButton: React.FC<MultipleMembersButtonProps> = ({
  members,
  showImpersonationModal,
  isDropdownOpen,
  onDropdownOpenChange,
  onMemberSelection,
}) => (
  <DropdownMenu open={isDropdownOpen} onOpenChange={onDropdownOpenChange}>
    <DropdownMenu.Trigger asChild={true}>
      <Button variant="solidSecondary">
        Log in as
        <Button.Icon name="CaretDown" />
      </Button>
    </DropdownMenu.Trigger>
    <DropdownMenu.Content align="end" side="bottom" css={{ minWidth: '240px' }}>
      {members.map(member => (
        <DropdownItem
          key={member.id}
          member={member}
          showImpersonationModal={showImpersonationModal}
          onMemberSelect={onMemberSelection}
        />
      ))}
    </DropdownMenu.Content>
  </DropdownMenu>
);

interface SingleMemberButtonProps {
  selectedMember: Client;
  showImpersonationModal: boolean;
  onSingleMemberLogin: () => void;
}

const SingleMemberButton: React.FC<SingleMemberButtonProps> = ({
  selectedMember,
  showImpersonationModal,
  onSingleMemberLogin,
}) => {
  if (showImpersonationModal) {
    return (
      <Button variant="solidSecondary" onClick={onSingleMemberLogin}>
        Log in
      </Button>
    );
  }

  return (
    <FormButton
      method="post"
      data-testId={`form-${selectedMember.id}`}
      action={urlBuilder.getImpersonateClientPage.absolute({ clientId: selectedMember.id })}
      shouldOpenNewTab={true}
      variant="solidSecondary"
    >
      Log in
    </FormButton>
  );
};

export const ImpersonationButton: React.FC<{ members: Client[]; showImpersonationModal: boolean }> = ({
  members,
  showImpersonationModal,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Client>(members[0]);
  const { openModal: openImpersonationModal } = useImpersonationModal();
  const impersonationLink = urlBuilder.getImpersonateClientPage.absolute({ clientId: selectedMember.id });

  const hasMultipleMembers = members.length > 1;

  const handleMemberSelection = (member: Client) => {
    setIsDropdownOpen(false);
    setSelectedMember(member);
    openImpersonationModal(impersonationLink);
  };

  if (hasMultipleMembers) {
    return (
      <MultipleMembersButton
        members={members}
        showImpersonationModal={showImpersonationModal}
        isDropdownOpen={isDropdownOpen}
        onDropdownOpenChange={setIsDropdownOpen}
        onMemberSelection={handleMemberSelection}
      />
    );
  }

  return (
    <SingleMemberButton
      selectedMember={selectedMember}
      showImpersonationModal={showImpersonationModal}
      onSingleMemberLogin={() => openImpersonationModal(impersonationLink)}
    />
  );
};
